/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTranslation } from 'react-i18next';

type TransFieldType<T extends string> = T | '' | `${string}:${T}`;
type tFunctionType = <T extends string>(transField: TransFieldType<T>, options?: any) => string;

/**
 * @param name Name of the translation file or an array of names.
 * @template T Type of the translation field, should be a string.
 * @description Custom hook to use translations with type safety.
 * It allows you to specify the translation file name(s) and provides a typed `t` function.
 * The `t` function will throw an error if the translation field does not match the specified file names.
 * It also logs a warning if the result is not a string.
 * @param options Optional parameters for the translation function.
 * @return An object containing the `t` function and other properties from `useTranslation`.
 */
export const useMyTranslations = <T extends string>(name: string[] | string) => {
  const { t: oldT, ...rest } = useTranslation(name);
  const t = (transField: TransFieldType<T>, options?: any): string => {
    if (transField.includes(':')) {
      const arr = Array.isArray(name) ? name : [name];
      if (!arr.some((item) => transField.includes(item))) throw new Error('Wrong translation file name');
    }
    const result = (oldT as tFunctionType)(transField, options);
    if (typeof result !== 'string') {
      console.warn(`'${transField}' returned '${result}', seems it is invalid result`);
      return '';
    }
    return result;
  };
  return { ...rest, t };
};
